import {
  DownOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Breadcrumb, Layout, Menu, Dropdown, Space } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../../routes";
import { REFRESH_TOKEN_NAME } from "../../shared/constant";
import { logout } from "../../redux/actions/userActions";
import { RootState } from "../../redux/rootReducer";
import {
  accountsItems,
  clapAdminManagerItems,
  managerItems,
} from "./MenuItems";
import AvatarView from "../_shared/AvatarView";
import AppHeader from "./AppHeader";
import {
  isAccounts,
  isClapAdminORClapManager,
  isManager,
} from "../../utils/clapUtils";
import useScreenSize from "../../hooks/useScreenSize";

const { Content, Sider } = Layout;
const siderWidth = 200;

const getMenuItems = (userDetails: any) => {
  if (isClapAdminORClapManager(userDetails)) return clapAdminManagerItems;
  if (isAccounts(userDetails)) return accountsItems;
  if (isManager(userDetails)) return managerItems;
  return [];
};

interface Props {
  children: JSX.Element;
}

function Navbar({ children }: Props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { screenSize } = useScreenSize();
  const [collapsed, setCollapsed] = useState(false);
  const { userDetails, logoutFulfilled } = useSelector(
    (state: RootState) => state.user,
  );
  const { client } = useSelector((state: RootState) => state.account);
  const { paymentDetails } = useSelector((state: RootState) => state.payment);
  const { invoiceDetails } = useSelector((state: RootState) => state.invoice);

  const handleMenu: MenuProps["onClick"] = (e) => {
    navigate(e.key);
  };

  const location = useLocation();

  // currently using only for client_details
  const stateValue: any = location.state;

  const breadcrumbNameMap: Record<string, string> = {
    [ROUTES.PATHS.ADMIN.SALESDATA]: "All Sales Data",
    [ROUTES.PATHS.ADMIN.TRACKS]: "Tracks",
    [ROUTES.PATHS.ADMIN.LABELS]: "Labels",
    [ROUTES.PATHS.ADMIN.ARTISTS]: "Artists",
    [ROUTES.PATHS.ADMIN.LYRICISTS]: "Lyricists",
    [ROUTES.PATHS.ADMIN.COMPOSERS]: "Composers",
    [ROUTES.PATHS.ADMIN.DISTRIBUTOR]: "Distributors",
    [ROUTES.PATHS.ADMIN.PLATFORM]: "Platforms",
    [ROUTES.PATHS.ADMIN.CLIENTS]: "All Clients",
    [ROUTES.PATHS.ADMIN.PAYMENTS]: "All Payments",
    [ROUTES.PATHS.ADMIN.INVOICES]: "All Invoices",
    [ROUTES.PATHS.ADMIN.INCOME]: "Reports",
    [ROUTES.PATHS.ADMIN.SIGNUP_REQUESTS]: "All Requests",
    [ROUTES.PATHS.ADMIN.USERS]: "All Users",
    [ROUTES.PATHS.ADMIN.SETTINGS]: "All Settings",
  };

  const pathSnippets = location.pathname.split("/").filter((i) => i);
  const parentRoute = `/${pathSnippets.slice(0, 1).join("/")}`;

  const breadcrumbItems = pathSnippets.map((_, index) => {
    const url = `/${pathSnippets.slice(0, index + 1).join("/")}`;
    const extractedName = pathSnippets[0].split("-").join(" ");

    // console.log("url: ", url);
    // console.log("pathSnippets: ", pathSnippets);
    // console.log("name Maped exist: ", breadcrumbNameMap[url]);
    // console.log("extractedName: ", extractedName);

    const getBreadcrumbSecArg = () => {
      switch (extractedName) {
        case "clients":
          return stateValue?.name || client?.name;
        case "payments":
          return paymentDetails?.title;
        case "invoices":
          return invoiceDetails ? invoiceDetails.invoice?.invoice_no : "";

        default:
          return pathSnippets[1];
      }
    };

    return (
      <React.Fragment key={url}>
        {pathSnippets.length === 1 && (
          <>
            <Breadcrumb.Item className="capitalize">
              {(() => {
                switch (url) {
                  case ROUTES.PATHS.ADMIN.TRACKS:
                  case ROUTES.PATHS.ADMIN.LABELS:
                  case ROUTES.PATHS.ADMIN.ARTISTS:
                  case ROUTES.PATHS.ADMIN.LYRICISTS:
                  case ROUTES.PATHS.ADMIN.COMPOSERS:
                  case ROUTES.PATHS.ADMIN.DISTRIBUTOR:
                  case ROUTES.PATHS.ADMIN.PLATFORM:
                    return "Catalog";
                  case ROUTES.PATHS.ADMIN.INCOME:
                    return "Report";

                  default:
                    return <span>{extractedName}</span>;
                }
              })()}
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <div>{breadcrumbNameMap[url]}</div>
            </Breadcrumb.Item>
          </>
        )}
        {pathSnippets.length > 1 && breadcrumbNameMap[url] && (
          <>
            <Breadcrumb.Item>
              <Link to={url} className="capitalize">
                {extractedName}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>{getBreadcrumbSecArg()}</span>
            </Breadcrumb.Item>
          </>
        )}
      </React.Fragment>
    );
  });

  useEffect(() => {
    if (screenSize.width < 1280) setCollapsed(true);
    else setCollapsed(false);
  }, [screenSize.width]);

  useEffect(() => {
    if (logoutFulfilled) {
      /* Logged out fulfilled */
      navigate(ROUTES.PATHS.LOGIN);
    }
  }, [logoutFulfilled]);

  const onClick: MenuProps["onClick"] = ({ key }) => {
    switch (key) {
      case "LOGOUT":
        dispatch(logout({ refresh: localStorage.getItem(REFRESH_TOKEN_NAME) }));
        break;

      default:
        break;
    }
  };

  const menu = (
    <Menu
      className="rounded-sm"
      onClick={onClick}
      items={[
        {
          label: <div>Logout</div>,
          key: "LOGOUT",
        },
        // {
        //   type: "divider",
        // },
      ]}
    />
  );

  const getDefaultOpenKeys = () => {
    if (isManager(userDetails)) return ["sub1"];
    if (isAccounts(userDetails)) return ["sub1", "sub2"];
    return [];
  };

  return (
    <Layout className="default-text min-h-screen">
      <AppHeader>
        <Dropdown overlay={menu} trigger={["click"]}>
          <button type="button">
            <Space>
              <AvatarView photo={userDetails?.profile_picture} />
              <div className="text-white text-sm">
                {userDetails?.name || userDetails?.groups[0].replace("_", " ")}
              </div>
              <DownOutlined style={{ fontSize: "11px", color: "white" }} />
            </Space>
          </button>
        </Dropdown>
      </AppHeader>
      <Layout hasSider className="bg-white">
        <Sider
          // collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          className="bg-ghost-white border-0 border-r border-solid border-light-silver fixed left-0 bottom-0 top-12 overflow-auto no-scrollbar"
        >
          <div className="logo" />
          <Menu
            selectedKeys={[
              location.pathname.includes(parentRoute) ? parentRoute : "",
            ]}
            mode="inline"
            // inlineIndent={16}
            // expandIcon={<YourIcon />}
            defaultOpenKeys={getDefaultOpenKeys()}
            items={userDetails ? getMenuItems(userDetails) : []}
            onClick={handleMenu}
            className="bg-ghost-white text-body14 pt-[29px]"
          />
          <div className="bg-lotion fixed bottom-0 z-10 max-w-[199px] w-[199px] resetborder-t border-flash-white">
            <div className="px-4 py-3">
              <div
                onClick={() => setCollapsed(!collapsed)}
                aria-hidden
                className="cursor-pointer"
              >
                {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              </div>
            </div>
          </div>
        </Sider>
        <Layout
          className="bg-white px-6 pt-6 pb-[30px] z-0"
          style={{
            marginLeft: collapsed ? 79.2 : siderWidth,
            marginTop: 48,
            transition: "all 0.2s ease 0s",
          }}
        >
          {location.pathname !== ROUTES.PATHS.ADMIN.HOME && (
            <Breadcrumb className="pb-4">{breadcrumbItems}</Breadcrumb>
          )}
          <Content>{children}</Content>
        </Layout>
      </Layout>
    </Layout>
  );
}

export default Navbar;
